import { Link } from "react-router-dom";
import { ArrowRight, Handshake, Building, Landmark, Users2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import StatCounter from "@/components/StatCounter";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { strengths, stats, heroImages } from "@/data/content";

const manifesto = [
  { num: "01", title: "Rigore analitico", text: "Nessuna decisione istintiva: ogni portafoglio viene studiato posizione per posizione, con modelli di valutazione consolidati e due diligence complete." },
  { num: "02", title: "Rapidità esecutiva", text: "Il tempo è valore, soprattutto nei crediti deteriorati. Processi snelli ci permettono di passare dall'analisi all'offerta in tempi ridotti." },
  { num: "03", title: "Trasparenza radicale", text: "Reportistica puntuale, comunicazione chiara e nessun costo nascosto: i nostri partner sanno sempre a che punto è ogni operazione." },
  { num: "04", title: "Orientamento al risultato", text: "Ogni strategia ha un obiettivo misurabile: massimizzare il recupero e il valore degli asset, posizione dopo posizione." },
];

const partnerTypes = [
  { icon: Landmark, label: "Banche e intermediari finanziari" },
  { icon: Building, label: "Fondi e investitori istituzionali" },
  { icon: Users2, label: "Imprese e società di gestione" },
];

const PercheNoi = () => (
  <div data-testid="perche-noi-page">
    <PageHero
      eyebrow="Perché Noi"
      lines={["Perché scegliere", <span key="i" className="italic text-gold">Vobis Credit</span>]}
      description="Un approccio che unisce rigore, rapidità e trasparenza: ecco cosa ci distingue nel mercato dei crediti deteriorati."
      image={heroImages.percheNoi}
    />

    {/* STATISTICHE ANIMATE */}
    <section className="relative overflow-hidden bg-navy-deep py-20" data-testid="perche-noi-stats">
      <div className="pointer-events-none absolute right-0 top-0 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-6 sm:grid-cols-2 lg:grid-cols-4 lg:px-10">
        {stats.map((s) => (
          <StatCounter key={s.label} {...s} />
        ))}
      </div>
    </section>

    {/* PUNTI DI FORZA */}
    <section className="bg-cream py-28" data-testid="punti-forza-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="I Punti di Forza"
          title="Sei ragioni per lavorare con noi"
          description="Esperienza, velocità e una consulenza davvero personalizzata: i pilastri su cui costruiamo ogni partnership."
        />
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {strengths.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.1}>
              <div data-testid={`strength-card-${i}`} className="group relative h-full overflow-hidden rounded-3xl bg-white p-9 shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-navy/10">
                <span className="pointer-events-none absolute -right-3 -top-6 font-display text-8xl italic text-navy/[0.05] transition-colors duration-500 group-hover:text-gold/15">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold transition-all duration-500 group-hover:bg-gold group-hover:text-navy">
                  <s.icon size={24} strokeWidth={1.8} />
                </span>
                <h3 className="relative mt-6 text-lg font-bold text-navy">{s.title}</h3>
                <p className="relative mt-3 text-sm leading-relaxed text-navy/60">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* PARTNERSHIP */}
    <section className="bg-white py-28" data-testid="partnership-section">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <div>
          <SectionHeading
            align="left"
            eyebrow="Partnership"
            title={<>Relazioni solide con i principali <span className="italic text-gold-dark">operatori del mercato</span></>}
          />
          <Reveal delay={0.12}>
            <p className="mt-6 leading-relaxed text-navy/65">
              La fiducia si costruisce sui risultati. Per questo banche, fondi, investitori e
              intermediari finanziari scelgono Vobis Credit come partner stabile per la cessione
              e la gestione dei propri crediti deteriorati.
            </p>
            <ul className="mt-8 space-y-4">
              {partnerTypes.map((p) => (
                <li key={p.label} className="flex items-center gap-4 rounded-2xl border border-navy/10 bg-cream px-5 py-4 transition-all duration-300 hover:border-gold/50">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy text-gold">
                    <p.icon size={18} strokeWidth={1.8} />
                  </span>
                  <span className="text-sm font-semibold text-navy">{p.label}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contatti"
              data-testid="partnership-cta"
              className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-navy transition-colors hover:text-gold-dark"
            >
              Diventa nostro partner
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="relative">
          <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/10">
            <img src={heroImages.handshake} alt="Partnership istituzionali" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
          <div className="absolute -bottom-6 right-8 flex items-center gap-3 rounded-2xl bg-gold px-7 py-5 shadow-xl">
            <Handshake size={24} className="text-navy" />
            <div>
              <div className="font-display text-xl text-navy">30+ partner</div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/60">istituzionali attivi</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* MANIFESTO / RISULTATI */}
    <section className="bg-navy py-28" data-testid="manifesto-section">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <SectionHeading dark eyebrow="Il Nostro Manifesto" title="Quattro principi, un solo standard: l'eccellenza" />
        <div className="mt-10">
          {manifesto.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.06}>
              <div data-testid={`manifesto-chapter-${m.num}`} className="group grid items-start gap-4 border-t border-white/10 py-10 transition-colors duration-500 last:border-b hover:bg-white/[0.03] md:grid-cols-[100px_1fr_2fr] md:gap-10 md:px-6">
                <span className="font-display text-4xl italic text-gold/60 transition-colors duration-500 group-hover:text-gold">{m.num}</span>
                <h3 className="font-display text-2xl text-white md:text-3xl">{m.title}</h3>
                <p className="text-sm leading-relaxed text-white/55 md:text-base">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CtaBand title="Mettici alla prova" text="Raccontaci il tuo portafoglio: ti mostreremo con i fatti cosa significa lavorare con Vobis Credit." />
  </div>
);

export default PercheNoi;
